/**
 * Google reCAPTCHA Enterprise — clés basées sur un score (invisible).
 * Doc: enterprise.js?render=SITE_KEY + grecaptcha.enterprise.execute()
 */

const SCRIPT_ATTR = 'data-recaptcha-enterprise';

export const RECAPTCHA_ACTION = 'submit_candidature';

function scriptSrc(siteKey) {
  return `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function loadRecaptchaEnterprise(siteKey) {
  if (!siteKey) return Promise.reject(new Error('siteKey manquant'));
  if (window.grecaptcha?.enterprise?.execute) {
    return Promise.resolve();
  }

  const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha?.enterprise) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Chargement enterprise.js échoué'))
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptSrc(siteKey);
    script.async = true;
    script.defer = true;
    script.setAttribute(SCRIPT_ATTR, 'true');
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Chargement enterprise.js échoué'));
    document.head.appendChild(script);
  });
}

async function executeOnce(siteKey, action) {
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise?.ready || !enterprise?.execute) {
    throw new Error('grecaptcha.enterprise indisponible');
  }
  await new Promise((resolve) => enterprise.ready(resolve));
  const token = await enterprise.execute(siteKey, { action });
  if (!token || typeof token !== 'string') {
    throw new Error('Jeton reCAPTCHA vide');
  }
  return token;
}

/**
 * @returns {Promise<string>} jeton (expire ~2 min)
 */
export async function executeRecaptchaEnterprise(siteKey, action = RECAPTCHA_ACTION) {
  await loadRecaptchaEnterprise(siteKey);

  let lastError;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      return await executeOnce(siteKey, action);
    } catch (err) {
      lastError = err;
      const detail = err?.message || String(err);
      if (!/timeout/i.test(detail) || attempt === 2) break;
      await delay(800);
    }
  }

  const detail = lastError?.message || String(lastError || '');
  if (/timeout/i.test(detail)) {
    throw new Error(
      'reCAPTCHA a expiré (réseau lent). Rechargez la page et réessayez.'
    );
  }
  throw new Error(
    detail || 'Échec reCAPTCHA. Rechargez la page et réessayez.'
  );
}
